import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import { UUID } from "uuidjs";
import merge from 'lodash.merge'
import transform from 'lodash.transform'
import { orderBase } from './orderBase.js'
import { translateFromItem } from './orderItemFunctions'
import okeResponse from 'okeoke.standardresponse'
import { OrderStates, OrderStatus } from '../consts.js'
import paymentsProcessor from './payments'
import invoicesProcessor from './invoices'
import statusesProcessor from './statuses'
import itemsProcessor from './items.js'
import vipProcessor from './vip.js'
import generalProcessor from './general.js'
import stateProcessor from './state.js'
import callbacksProcessor from './callbacks'
import discountsProcessor from './discounts'
import itemDiscountsProcessor from './itemDiscounts'
import externalPaymentsProcessor from './externalPayments'
import ordereduuid from '../Helpers/timeuuid'

var Order = function (client, clientSocket, catalog, offlineEnabled = false, app = null, deviceInfo = null) {
  this.uid = UUID.generate()

  this.deviceInfo = deviceInfo

  this.order = cloneDeep(orderBase)
  this.assembledOrderItems = {}

  this.discounts = new discountsProcessor(this, offlineEnabled, clientSocket, catalog)
  this.itemDiscounts = new itemDiscountsProcessor(this, catalog, client)

  this.payments = new paymentsProcessor(this, offlineEnabled, clientSocket, client, deviceInfo)
  this.invoices = new invoicesProcessor(this, offlineEnabled, clientSocket, client, deviceInfo)
  this.statuses = new statusesProcessor(this, offlineEnabled, clientSocket, client)
  this.items = new itemsProcessor(this, offlineEnabled, clientSocket, catalog)
  this.vip = new vipProcessor(this, offlineEnabled, clientSocket)
  this.general = new generalProcessor(this, offlineEnabled, clientSocket, catalog)
  this.state = new stateProcessor(this, catalog, client)
  this.callbacks = new callbacksProcessor()

  this.externalPayments = new externalPaymentsProcessor(this, offlineEnabled)

  this.printSlips = false

  let orderSnapshot = null
  let orderOfflineChanges = null
  let updateTimeout = null

  let updates = []
  let processing = false

  const addUpdateToQueue = (updateData) => {
    updates.push(updateData)
    setTimeout(processUpdates, 10)
  }

  const processUpdate = (data) => {
    if (orderSnapshot == null) {
      orderSnapshot = cloneDeep(this.order)
      orderOfflineChanges = cloneDeep(this.order)
    }
    if (data.items != null) {
      mergeAccumulatedItemUpdates(data.items)
      delete data.items
    }
    if (data.discounts != null) {
      mergeAccumulatedDiscountUpdates(data.discounts)
      delete data.discounts
    }
    if (data.itemDiscounts != null) {
      mergeAccumulatedItemDiscountUpdates(data.itemDiscounts)
      delete data.itemDiscounts
    }
    if (data.statuses != null) {
      mergeAccumulatedStatusUpdates(data.statuses)
      delete data.statuses
    }
    if (data.payments != null) {
      mergeAccumulatedPaymentUpdates(data.payments)
      delete data.payments
    }
    if (data.externalPayments != null) {
      mergeAccumulatedExternalPaymentUpdates(data.externalPayments)
      delete data.externalPayments
    }
    if (data.invoices != null) {
      mergeAccumulatedInvoiceUpdates(data.invoices)
      delete data.invoices
    }
    merge(this.order, data)
    merge(orderOfflineChanges, data)
    clearTimeout(updateTimeout)
    updateTimeout = setTimeout(commitUpdates, 100)
  }

  const processUpdates = () => {
    if (processing) return null
    processing = true
    while (updates?.length > 0) {
      let data = updates.shift()
      processUpdate(data)
    }
    this.state.reBuildAssembledItems()
    processing = false
    return true
  }

  const mergeAccumulatedItemUpdates = (items) => {
    for (let item of Object.values(items)) {
      this.items.insertUpdateItem(item)
      if (orderOfflineChanges) {
        this.items.insertUpdateItem(item, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedDiscountUpdates = (discounts) => {
    for (let discount of Object.values(discounts)) {
      this.discounts.setDiscount(discount, null, false)
      if (orderOfflineChanges) {
        this.discounts.setDiscount(discount, orderOfflineChanges, false)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedItemDiscountUpdates = (itemDiscounts) => {
    for (let itemDiscount of Object.values(itemDiscounts)) {
      this.itemDiscounts.applyItemDiscount(itemDiscount, null)
      if (orderOfflineChanges) {
        this.itemDiscounts.applyItemDiscount(itemDiscount, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedStatusUpdates = (statuses) => {
    //console.log('mergeAccumulatedStatusUpdates', statuses, orderOfflineChanges)
    for (let status of Object.values(statuses)) {
      this.statuses.insertFromCloud(status)
      if (orderOfflineChanges) {
        this.statuses.insertFromCloud(status, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedPaymentUpdates = (payments) => {
    //console.log('mergeAccumulatedPaymentUpdates', payments, orderOfflineChanges)
    for (let payment of Object.values(payments)) {
      this.payments.insertFromCloud(payment)
      if (orderOfflineChanges) {
        this.payments.insertFromCloud(payment, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedExternalPaymentUpdates = (externalPayments) => {
    //console.log('mergeAccumulatedPaymentUpdates', payments, orderOfflineChanges)
    for (let payment of Object.values(externalPayments)) {
      this.externalPayments.insertFromCloud(payment)
      if (orderOfflineChanges) {
        this.externalPayments.insertFromCloud(payment, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const mergeAccumulatedInvoiceUpdates = (invoices) => {
    //console.log('mergeAccumulatedInvoiceUpdates', invoices, orderOfflineChanges)
    for (let invoice of Object.values(invoices)) {
      this.invoices.insertFromCloud(invoice)
      if (orderOfflineChanges) {
        this.invoices.insertFromCloud(invoice, orderOfflineChanges)
      } else {
        //console.log('No offline changes order object.')
      }
    }
  }

  const commitUpdates = () => {
    //console.log('commitUpdates', cloneDeep(this.order))
    let diff = difference(orderOfflineChanges, orderSnapshot)
    // let eq = isEqual(orderOfflineChanges, orderSnapshot)
    orderSnapshot = null
    orderOfflineChanges = null
    if (Object.keys(diff || {}).length > 0) {
      this.save()
    }
  }

  this.addUpdate = (data) => {
    addUpdateToQueue(data)
  }

  const syncToServer = () => {
    return new Promise((resolve, reject) => {
      if (this.order.uuid == null) {
        resolve({ success: false, message: 'No started order...' })
        return
      }
      if (!clientSocket.authorized) {
        resolve({ success: false, message: 'Client not connected!' })
      } else {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 2000)
        clientSocket.ioclient.emit(
          'updateOrderFromClient',
          {
            order: this.order
          },
          (e) => {
            resolve(e)
          }
        )
      }
    })
  }

  const syncFromCloud = (orderUUID) => {
    return new Promise((resolve, reject) => {
      if (!clientSocket.authorized) { resolve(okeResponse(false, 503, 'Not connected', {})) }
      setTimeout(() => {
        resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
      }, 3000)
      clientSocket.ioclient.emit(
        'getOrder',
        {
          uuid: orderUUID
        },
        (res) => {
          if (res.success) {
            this.order.uuid = res.data.uuid
            this.onOrderUpdated(res.data)
          }
          resolve(res)
        }
      )
    })
  }

  this.save = async () => {
    if (clientSocket.authorized) {
      let res = await syncToServer()
      if (!res.success) {
        this.order.offline = 1
        this.callbacks.callCallbacks('onOrderUpdated', this)
      } else {
        if (this.order.offline === 1) {
          this.order.offline = 0
          this.callbacks.callCallbacks('onOrderUpdated', this)
        }
      }
    } else {
      this.order.offline = 1
      this.callbacks.callCallbacks('onOrderUpdated', this)
    }
  }

  this.loadFromIDB = (onlineOrder) => {
    if (onlineOrder?.uuid == null) return false
    merge(this.order, onlineOrder)
    this.state.reBuildAssembledItems()
    return true
  }

  this.loadOffline = (offlineOrder) => {
    if (offlineOrder?.uuid == null) return false
    merge(this.order, offlineOrder)
    //console.log('LOAD OFFLINE', this.order.orderNumber, this.order.orderState)
    this.state.reBuildAssembledItems()
    syncFromCloud(offlineOrder.uuid)
    return true
  }

  this.load = async (uuid = null) => {
    if (uuid == null) return false
    let orderLoaded = false
    if (clientSocket.authorized) {
      try {
        let orderRes = await syncFromCloud(uuid)
        if (orderRes.success) {
          return true
        } else {
          return false
        }
      } catch (e) {
        console.error('syncFromCloud: ', e)
        return orderLoaded
      }
    } else {
      return orderLoaded
    }
  }

  this.updateBasketItemSize = (orderItemUUID, selectedSizeUUID) => {
    if (this.order.orderState !== OrderStates.BASKET) {
      //console.log('Order not basket', this.order.orderState)
      return null
    }
    if (this.order.uuid == null) {
      //console.log('Order not inited')
      return null
    }
    let existingItem = this.items.getItem(orderItemUUID)
    //console.log(orderItemUUID, count, existingItem)
    if (existingItem != null) {
      this.addUpdate({ items: { [orderItemUUID]: { ...existingItem, selectedSizeUUID } } })
      return orderItemUUID
    } else {
      return null
    }
  }

  this.updateBasketItemCount = (orderItemUUID, count) => {
    if (this.order.orderState !== OrderStates.BASKET) {
      return null
    }
    if (this.order.uuid == null) {
      return null
    }
    let existingItem = this.items.getItem(orderItemUUID)
    if (existingItem != null) {
      this.addUpdate({ items: { [orderItemUUID]: { ...existingItem, count } } })
      // let children = this.items.getItemsByParent(orderItemUUID)
      // for (let child of children) {
      //   if (child.count !== 0) {
      //     this.addUpdate({ items: { [child.orderItemUUID]: { ...child, count } } })
      //   }
      // }
      return orderItemUUID
    } else {
      return null
    }
  }

  this.updateBasketItemQuantity = (orderItemUUID, quantity) => {
    if (this.order.uuid == null) {
      return null
    }
    let existingItem = this.items.getItem(orderItemUUID)
    if (existingItem != null) {
      let { sizeAttributes = null, unitType, package: itemPackage = null } = this.assembledOrderItems[orderItemUUID] || {}
      if (sizeAttributes?.scale && unitType === 'kg' && +itemPackage?.weight > 0) {
        if (+itemPackage.weight < +quantity) {
          quantity = +quantity - +itemPackage.weight
        }
      }
      this.addUpdate({ items: { [orderItemUUID]: { ...existingItem, quantity: +((+quantity || 0).toFixed(3)) } } })
      return orderItemUUID
    } else {
      return null
    }
  }

  this.updateBasketItem = (item, categoryUUID, parentOrderItemUUID) => {
    if (this.order.orderState !== OrderStates.BASKET) {
      return null
    }
    if (this.order.uuid == null) {
      return null
    }
    const isOrderItem = item.modifierGroups == null
    let existingItem = this.items.getItem(item.orderItemUUID)
    if (existingItem != null) {
      // Update
      let updatedItem = isOrderItem
        ? this.items.cleanOrderItem(item)
        : translateFromItem(item, categoryUUID)
      updatedItem.updateUID = this.uid
      this.addUpdate({ items: { [item.orderItemUUID]: updatedItem } })
      // if (existingItem.count !== updatedItem.count) {
      //   let children = this.items.getItemsByParent(item.orderItemUUID)
      //   for (let child of children) {
      //     if (child.count !== 0) {
      //       this.addUpdate({ items: { [child.orderItemUUID]: { ...child, count } } })
      //     }
      //   }
      // }
      return item.orderItemUUID
    } else {
      // Insert
      let newItem = isOrderItem
        ? this.items.cleanOrderItem(item)
        : translateFromItem(item, categoryUUID)
      newItem.orderItemUUID = ordereduuid()
      newItem.parentOrderItemUUID = parentOrderItemUUID || null
      newItem.deviceUUID = this.deviceInfo?.deviceUUID || null
      newItem.sysUserUUID = client?.localUser?.userUUID || null
      newItem.insertTimestamp = new Date().getTime()
      newItem.updateUID = this.uid
      this.addUpdate({ items: { [newItem.orderItemUUID]: newItem } })
      return newItem.orderItemUUID
    }
  }

  // Start new basket/order
  this.start = (cloneOrder = null, locationUUID = null, tableUUID = null, takeAway = null, sandbox = false, paymentType = null) => {
    if (cloneOrder != null) {
      const order = cloneDeep(cloneOrder)
      delete order.orderNumber
      merge(this.order, order)
    } else {
      if (tableUUID != null) this.order.tableUUID = tableUUID
      else if (locationUUID != null) this.order.locationUUID = locationUUID
      this.order.takeAway = takeAway
      this.order.sandbox = sandbox
      this.order.paymentType = paymentType
    }
    this.order.uuid = ordereduuid()
    this.order.insertDeviceUUID = this.deviceInfo?.deviceUUID || null
    this.order.insertDeviceType = this.deviceInfo?.deviceType || 1000
    let location = client.locationsModel.getLocation(this.order.locationUUID)
    if (app !== 'brandApp') {
      this.order.serviceFee = {
        type: location?.localConfig?.serviceFee?.type || 0,
        rounding: location?.localConfig?.serviceFee?.rounding || 0,
        percent: location?.localConfig?.serviceFee?.percent?.[app] || 0
      }
      this.order.tippType = location?.localConfig?.tippType || 0
    }
    this.itemDiscounts.autoApplyItemDiscounts()
    this.state.reBuildAssembledItems()
    return this.order.uuid
  }

  this.abort = async (reasonCode = 0, reasonMessage = '') => {
    if (this.order.orderState === OrderStates.BASKET) {
      let res = await this.statuses.addStatus(
        OrderStatus.ORDER_CLOSED_CANCEL_BASKET,
        { reasonCode, reasonMessage }
      )
      return res
    } else {
      let res = await this.statuses.addStatus(
        OrderStatus.ORDER_CLOSED_FAIL_CUSTOMER_CANCELLED,
        { reasonCode, reasonMessage }
      )
      return res
    }
  }

  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  this.waitIfSyncing = async (depth = 0) => {
    if (depth > 10) return false
    if (app !== 'brandApp') return true
    if (orderOfflineChanges != null) {
      console.log('Waiting for sync to finish: ', status)
      await delay(1000)
      let res = await this.waitIfSyncing(depth + 1)
      return res
    } else {
      return true
    }
  }

  this.submit = async (paymentType = null) => {
    if (this.order.locationUUID == null) { return okeResponse(false, 0, 'No location defined', null) }
    if (this.order.orderState > OrderStates.BASKET) {
      return okeResponse(false, 0, 'Already submitted', null)
    }
    if (this.order.itemCount < 1) {
      return okeResponse(false, 0, 'No items in basket', null)
    }
    this.general.setPaymentType(paymentType)
    let okToSubmit = await this.waitIfSyncing()
    if (okToSubmit) {
      this.submitHappened = true
      this.waitingForExternalNumber = true
      let res = await this.statuses.addStatus(OrderStatus.ORDER_INSERTED, '')
      return res
    } else {
      return okeResponse(false, 0, 'Sync error', null)
    }
  }

  this.onConnectionChange = (data) => {
    // console.log(data)
    switch (data.state) {
      case 'Authorized':
        if (this.order.offline) this.save()
        break
      default:
    }
  }

  function isObject(obj) {
    return obj === Object(obj) || Array.isArray(obj)
  }

  function removeNullValues(object) {
    return transform(object, function (result, value, key) {
      if (value != null) {
        result[key] = value
      }
    })
  }

  function difference(object, base) {
    function changes(object, base) {
      return transform(object, function (result, value, key) {
        if (!isEqual(value, base[key])) {
          result[key] =
            isObject(value) && isObject(base[key])
              ? changes(value, base[key])
              : value
        }
      })
    }
    return changes(object, base)
  }

  function deleteArrayFields(theOrder) {
    let noArrayOrder = cloneDeep(theOrder)
    delete noArrayOrder.invoices
    delete noArrayOrder.statuses
    delete noArrayOrder.payments
    delete noArrayOrder.items
    delete noArrayOrder.discounts
    delete noArrayOrder.itemDiscounts
    delete noArrayOrder.externalPayments
    return noArrayOrder
  }

  function cleanOrder(_order) {
    for (let key in _order) {
      if (!(key in orderBase)) {
        // console.log('ORDER DELKEY:', key)
        delete _order[key]
      }
    }
    return _order
  }

  this.onOrderUpdated = async (onlineOrder) => {
    if (onlineOrder.uuid !== this.order.uuid) {
      return null
    }
    try {
      let changesInArrays = 0
      let currentOrder = removeNullValues(cloneDeep(this.order))
      currentOrder = cleanOrder(currentOrder)
      let newOrder = removeNullValues(onlineOrder)
      newOrder = cleanOrder(newOrder)

      let eq = isEqual(deleteArrayFields(currentOrder), deleteArrayFields(newOrder))
      let diff = difference(deleteArrayFields(newOrder), deleteArrayFields(currentOrder))

      for (let [key, value] of Object.entries(diff)) {
        if (isObject(value)) {
          this.order[key] = { ...onlineOrder[key] }
        } else {
          this.order[key] = value
        }
      }

      try {
        for (let item of onlineOrder.items || []) {
          if (item.updateUID !== this.uid) {
            let exsitingItem = this.items.getItem(item.orderItemUUID)
            let newItem = this.items.cleanOrderItem(item)
            if (!exsitingItem) {
              this.items.insertUpdateItem(newItem)
              changesInArrays++
            } else {
              if (!isEqual(newItem, this.items.cleanOrderItem(exsitingItem))) {
                this.items.insertUpdateItem(newItem)
                changesInArrays++
              }
            }
          }
        }
      } catch (e) {
        console.error(e)
      }
      for (let status of onlineOrder.statuses || []) {
        changesInArrays += (this.statuses.insertFromCloud(status) != null ? 1 : 0)
      }
      for (let payment of onlineOrder.payments || []) {
        changesInArrays += (this.payments.insertFromCloud(payment) != null ? 1 : 0)
      }
      for (let payment of onlineOrder.externalPayments || []) {
        changesInArrays += this.externalPayments.set(payment, true)
      }
      for (let invoice of onlineOrder.invoices || []) {
        changesInArrays += (this.invoices.insertFromCloud(invoice) != null ? 1 : 0)
      }
      for (let discount of onlineOrder.discounts || []) {
        let existing = this.discounts.getDiscount(discount.type, discount.id)
        if (existing == null) {
          this.discounts.setDiscount(discount, null, false)
          changesInArrays++
        } else {
          if (!isEqual(discount, existing)) {
            this.discounts.setDiscount(discount, null, false)
            changesInArrays++
          }
        }
      }
      for (let itemDiscount of onlineOrder.itemDiscounts || []) {
        let applyRes = this.itemDiscounts.applyItemDiscount(itemDiscount)
        if (applyRes.code === 201) {
          changesInArrays++
        }
      }
      if (!eq || changesInArrays > 0) {
        this.state.reBuildAssembledItems()
      }
      return this.order
    } catch (e) {
      console.error(e)
      return null
    }
  }

  this.addCallback = this.callbacks.addCallback
  this.removeCallback = this.callbacks.removeCallback

  this.deinit = () => {
    clientSocket.removeCallback('onConnectionChange', this.uid)
    clientSocket.removeCallback('onOrderUpdated', this.uid)
  }

  clientSocket.addCallback('onConnectionChange', this.uid, this.onConnectionChange)
  clientSocket.addCallback('onOrderUpdated', this.uid, this.onOrderUpdated)
}

export { Order }
